import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

export const LATEST_ONLY_HEADER = 'X-Skylark-Latest-Only';
export type LatestOnlyScope = 'path' | 'pathAndParams';

@Injectable()
export class LatestOnlyHttpInterceptor implements HttpInterceptor {
  private pendingRequests = new Map<string, Subject<void>>();

  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const scope = req.headers.get(LATEST_ONLY_HEADER) as LatestOnlyScope | null;

    // No flag means this request should run normally.
    if (!scope) {
      return next.handle(req);
    }

    // ----- NORMALIZE REQUEST -----
    // Remove the internal header before sending the request to the API.
    const normalizedRequest = req.clone({
      headers: req.headers.delete(LATEST_ONLY_HEADER),
    });
    const key = this.buildRequestKey(normalizedRequest, scope);

    // ----- CANCEL ANY OLDER MATCHING REQUEST -----
    const existing = this.pendingRequests.get(key);
    if (existing) {
      existing.next();     // triggers the 'cancel' takeUntil on the previous observable
      existing.complete(); // tidy up the Subject
    }

    // ----- CREATE CANCELLER -----
    const cancel$ = new Subject<void>();
    this.pendingRequests.set(key, cancel$); // stores this requests 'cancel' against the key for later requests to access

    // ----- FORWARD REQUEST -----
    return next.handle(normalizedRequest).pipe(
      takeUntil(cancel$),
      finalize(() => {
        const current = this.pendingRequests.get(key);

        if (current === cancel$) {
          this.pendingRequests.delete(key); // slate wiped for this key because latest request finished
        }

        cancel$.complete(); // complete the subject to free up subscribers
      })
    );
  }

  /**
   * Creates a cancellation key used to identify requests:
   * - 'path': all calls to the same path share the key, even when query params differ.
   * - 'pathAndParams': include the query string so only identical URLs cancel each other.
   */
  private buildRequestKey(req: HttpRequest<any>, scope: LatestOnlyScope): string {
    const pathOnly = req.url.split('?')[0];
    const url = scope === 'path' ? pathOnly : req.urlWithParams;
    return `${req.method}::${url}`;
  }
}
